/**
 * Global `/api/*` 401 handling.
 *
 * A 401 on an authenticated API call means the session is stale or gone.
 * Instead of letting it surface as a generic query error, the registered
 * handler clears the query cache, revokes the session best-effort, and
 * routes to `/login`. The signed-out `/login` page issues no authenticated
 * API calls, and sign-out is idempotent, so a repeat 401 cannot spiral into
 * a loop.
 */
type UnauthorizedHandler = () => void;

let currentHandler: UnauthorizedHandler | undefined;
let handling = false;

/**
 * Register the global 401 handler. Returns an unsubscribe function. Only one
 * handler is active at a time; registering a new one replaces the previous.
 */
export function setUnauthorizedHandler(handler: UnauthorizedHandler): () => void {
  currentHandler = handler;
  return () => {
    if (currentHandler === handler) {
      currentHandler = undefined;
    }
  };
}

/**
 * Invoke the registered 401 handler, if any. A burst of parallel 401s
 * collapses into one handler call: the `handling` latch is held only for the
 * synchronous duration of the call, then released so a later session in the
 * same page load can still trigger sign-out.
 */
export function notifyUnauthorized(): void {
  if (handling || !currentHandler) return;
  handling = true;
  try {
    currentHandler();
  } finally {
    handling = false;
  }
}

/** Test-only: reset the module state between cases. */
export function resetUnauthorizedHandler(): void {
  currentHandler = undefined;
  handling = false;
}
